import { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface CardProps {
  children: ReactNode
  className?: string
  padding?: boolean
}

export function Card({ children, className, padding = true }: CardProps) {
  return (
    <div
      className={cn(
        'bg-card text-card-foreground rounded-lg border border-border shadow-elevation-1',
        padding && 'p-6',
        className
      )}
    >
      {children}
    </div>
  )
}

export interface CardHeaderProps {
  title?: string
  description?: string
  action?: ReactNode
  className?: string
  children?: ReactNode
}

export function CardHeader({ title, description, action, className, children }: CardHeaderProps) {
  // If children are provided, render them directly (shadcn/ui style)
  if (children) {
    return (
      <div className={cn('flex flex-col space-y-1.5 pb-4', className)}>
        {children}
      </div>
    )
  }

  // Otherwise use the props-based API
  return (
    <div className={cn('flex items-start justify-between mb-4', className)}>
      <div>
        {title && <h3 className="text-lg font-semibold text-foreground">{title}</h3>}
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  )
}

export interface CardContentProps {
  children: ReactNode
  className?: string
}

export function CardContent({ children, className }: CardContentProps) {
  return <div className={className}>{children}</div>
}

export interface CardFooterProps {
  children: ReactNode
  className?: string
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div className={cn('mt-6 pt-4 border-t border-border', className)}>
      {children}
    </div>
  )
}

export interface CardTitleProps {
  children: ReactNode
  className?: string
}

export function CardTitle({ children, className }: CardTitleProps) {
  return (
    <h3 className={cn('text-lg font-semibold text-foreground', className)}>
      {children}
    </h3>
  )
}

export interface CardDescriptionProps {
  children: ReactNode
  className?: string
}

export function CardDescription({ children, className }: CardDescriptionProps) {
  return (
    <p className={cn('text-sm text-muted-foreground', className)}>
      {children}
    </p>
  )
}
